import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneLight,
  oneDark,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { formatTimestamp } from './formatTimestamp';
import { highlightSearchTerm } from './highlightSearch';

export interface MarkdownComponentsOptions {
  /** Files data for resolving citations */
  files?: {
    data: Array<{
      _id: string;
      name?: string;
      teamId?: string;
    }>;
  };
  /** Whether to hide reference links */
  hideReferences?: boolean;
  /** Search term to highlight in content */
  searchTerm?: string;
  /** Callback for timestamp clicks (for in-video navigation) */
  onTimestampClick?: (seconds: number) => void;
}

/**
 * Helper to safely process children for search highlighting
 * Handles arrays, strings, and React elements properly
 */
const processChildren = (
  children: React.ReactNode,
  searchTerm?: string,
): React.ReactNode => {
  if (!searchTerm) return children;

  if (typeof children === 'string') {
    return highlightSearchTerm(children, searchTerm);
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return (
          <React.Fragment key={index}>
            {highlightSearchTerm(child, searchTerm)}
          </React.Fragment>
        );
      }
      return child;
    });
  }

  return children;
};

/**
 * Creates standardized ReactMarkdown components with consistent styling
 * Used by both MultiAskAI and SelectedVideoChat for rendering markdown content
 *
 * @param options - Configuration options for markdown rendering
 * @returns Object containing ReactMarkdown component overrides
 */
export const createMarkdownComponents = (
  options: MarkdownComponentsOptions = {},
) => {
  const {
    files,
    hideReferences = false,
    searchTerm,
    onTimestampClick,
  } = options;

  // Detect dark mode for syntax highlighting
  const isDarkMode =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  return {
    h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
      <h1 className="text-lg font-bold mt-5 first:mt-0 mb-2" {...props}>
        {processChildren(children, searchTerm)}
      </h1>
    ),
    h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
      <h2 className="text-base font-bold mt-4 first:mt-0 mb-2" {...props}>
        {processChildren(children, searchTerm)}
      </h2>
    ),
    h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
      <h3
        className="text-[0.9375rem] font-bold mt-3.5 first:mt-0 mb-1.5"
        {...props}
      >
        {processChildren(children, searchTerm)}
      </h3>
    ),
    h4: ({ children, ...props }: React.ComponentPropsWithoutRef<'h4'>) => (
      <h4 className="text-sm font-bold mt-3 first:mt-0 mb-1.5" {...props}>
        {processChildren(children, searchTerm)}
      </h4>
    ),
    h5: ({ children, ...props }: React.ComponentPropsWithoutRef<'h5'>) => (
      <h5
        className="text-[0.8125rem] font-bold mt-2.5 first:mt-0 mb-1"
        {...props}
      >
        {processChildren(children, searchTerm)}
      </h5>
    ),
    h6: ({ children, ...props }: React.ComponentPropsWithoutRef<'h6'>) => (
      <h6 className="text-xs font-bold mt-2 first:mt-0 mb-1" {...props}>
        {processChildren(children, searchTerm)}
      </h6>
    ),
    p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => {
      // Check if paragraph starts with bold text (likely a section header like "2. Title")
      // These need extra top margin for visual separation
      const firstChild = Array.isArray(children) ? children[0] : children;
      const startsWithBold =
        firstChild &&
        typeof firstChild === 'object' &&
        'type' in firstChild &&
        (firstChild.type === 'strong' || firstChild.type?.name === 'strong');

      return (
        <p
          className={`break-words whitespace-pre-wrap mb-3 last:mb-0 text-sm leading-normal [li>&]:mb-0 ${
            startsWithBold ? 'mt-4 first:mt-0' : ''
          }`}
          {...props}
        >
          {processChildren(children, searchTerm)}
        </p>
      );
    },
    strong: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'strong'>) => (
      <strong
        style={{
          fontWeight: 600,
          display: 'inline',
        }}
        {...props}
      >
        {processChildren(children, searchTerm)}
      </strong>
    ),
    em: ({ children, ...props }: React.ComponentPropsWithoutRef<'em'>) => (
      <em
        style={{
          fontStyle: 'italic',
          display: 'inline',
        }}
        {...props}
      >
        {processChildren(children, searchTerm)}
      </em>
    ),
    ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul className="my-2 pl-6 list-disc" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol className="my-2 pl-6 list-decimal" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
      <li className="text-sm leading-normal my-1.5" {...props}>
        {processChildren(children, searchTerm)}
      </li>
    ),
    table: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'table'>) => (
      <table
        className="mb-4"
        style={{ borderCollapse: 'collapse', width: '100%' }}
        {...props}
      >
        {children}
      </table>
    ),
    th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
      <th
        style={{
          fontSize: '0.875rem',
          border: '1px solid hsl(var(--border))',
          padding: '6px',
          backgroundColor: 'hsl(var(--muted))',
        }}
        {...props}
      >
        {processChildren(children, searchTerm)}
      </th>
    ),
    td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
      <td
        style={{
          fontSize: '0.875rem',
          border: '1px solid hsl(var(--border))',
          padding: '6px',
        }}
        {...props}
      >
        {processChildren(children, searchTerm)}
      </td>
    ),
    pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) => (
      <pre
        className="bg-muted/50 dark:bg-muted/30 p-4 my-3 rounded-md overflow-auto"
        {...props}
      >
        {children}
      </pre>
    ),
    code: ({
      children,
      className,
      ...props
    }: React.ComponentPropsWithoutRef<'code'> & { className?: string }) => {
      // Check if this is a code block (has language class) vs inline code
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : null;

      // For code blocks with a language, use syntax highlighting
      if (language) {
        const codeString = String(children).replace(/\n$/, '');
        return (
          <SyntaxHighlighter
            style={isDarkMode ? oneDark : oneLight}
            language={language}
            PreTag="div"
            customStyle={{
              margin: 0,
              padding: '1rem',
              borderRadius: '0.375rem',
              fontSize: '0.8125rem',
              lineHeight: '1.5',
            }}
          >
            {codeString}
          </SyntaxHighlighter>
        );
      }

      // For inline code or code blocks without language
      return (
        <code
          className={cn(
            'bg-muted/70 dark:bg-muted/50 px-1.5 py-0.5 rounded text-[0.8125rem] font-mono',
            className,
          )}
          {...props}
        >
          {children}
        </code>
      );
    },
    blockquote: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'blockquote'>) => (
      <blockquote
        style={{
          fontSize: '0.875rem',
          borderLeft: '3px solid hsl(var(--border))',
          marginLeft: 0,
          paddingLeft: '12px',
          fontStyle: 'italic',
          marginBottom: '0.75rem',
        }}
        {...props}
      >
        {processChildren(children, searchTerm)}
      </blockquote>
    ),
    hr: ({ ...props }: React.ComponentPropsWithoutRef<'hr'>) => (
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid hsl(var(--border))',
          margin: '1em 0',
        }}
        {...props}
      />
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // Handle file reference format: [→](fileId@timestamp)
      if (href?.includes('@')) {
        if (hideReferences) return null;
        const [fileId, timestamp] = href.split('@');
        const file = files?.data.find((f) => f._id === fileId);

        // If we have a timestamp click handler (single video mode)
        if (onTimestampClick) {
          return (
            <button
              type="button"
              onClick={() => onTimestampClick(Number(timestamp))}
              className={cn(
                'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
              )}
              title={`Jump to ${formatTimestamp(timestamp)}`}
            >
              <ArrowUpRight
                size={18}
                className={cn(
                  'relative -top-0.5 inline-block px-[2px] align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
                )}
                strokeWidth={1.5}
              />
            </button>
          );
        }

        // Multi-file mode - open in new window
        return (
          <button
            type="button"
            onClick={() => {
              const baseUrl = window.location.pathname.includes('/app')
                ? '/app/#'
                : '/#';
              window.open(
                `${baseUrl}/library/${file?.teamId || 'unknown'}/recents/${fileId}?t=${timestamp}`,
                '_blank',
              );
            }}
            className={cn(
              'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
            )}
            title={
              file
                ? `Go to: ${file.name} at ${formatTimestamp(timestamp)}`
                : 'Go to source...'
            }
          >
            <ArrowUpRight
              size={18}
              className={cn(
                'relative -top-0.5 inline-block px-[2px] align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
              )}
              strokeWidth={1.5}
            />
          </button>
        );
      }

      // Regular external links
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {processChildren(children, searchTerm)}
        </a>
      );
    },
  };
};
